import type { CommuteDestination } from '@/src/lib/hooks/neighborhood';

type PlacePrediction = {
  placeId: string;
  description: string;
  primaryText: string;
  secondaryText: string;
};

let autocompleteService: google.maps.places.AutocompleteService | null = null;
let geocoder: google.maps.Geocoder | null = null;

function ensureServices(): boolean {
  if (typeof google === 'undefined' || !google.maps?.places) return false;

  if (!autocompleteService) {
    autocompleteService = new google.maps.places.AutocompleteService();
  }
  if (!geocoder) {
    geocoder = new google.maps.Geocoder();
  }
  return true;
}

export type PlacePredictionOptions = Pick<google.maps.places.AutocompletionRequest, 'types' | 'componentRestrictions'>;

// Named places only — the HDP Neighborhood module's historical behavior.
// Callers that need street addresses (e.g. the SRP commute filter) pass their
// own options; an explicit options object fully replaces this default.
const DEFAULT_PREDICTION_OPTIONS: PlacePredictionOptions = { types: ['establishment'] };

export async function getPlacePredictions(
  input: string,
  options: PlacePredictionOptions = DEFAULT_PREDICTION_OPTIONS,
): Promise<Array<PlacePrediction>> {
  if (!input.trim() || !ensureServices() || !autocompleteService) return [];

  return new Promise((resolve) => {
    autocompleteService!.getPlacePredictions({ input, ...options }, (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
        resolve([]);
        return;
      }
      resolve(
        predictions.map((p) => ({
          placeId: p.place_id,
          description: p.description,
          primaryText: p.structured_formatting.main_text,
          secondaryText: p.structured_formatting.secondary_text,
        })),
      );
    });
  });
}

export async function getPlaceDetails(placeId: string, description: string): Promise<CommuteDestination> {
  if (!ensureServices() || !geocoder) {
    throw new Error('Google Maps services not available');
  }

  const result = await geocoder.geocode({ placeId });

  if (!result.results[0]) {
    throw new Error('Place not found');
  }

  const location = result.results[0].geometry.location;
  return {
    placeId,
    name: description,
    address: result.results[0].formatted_address,
    lat: location.lat(),
    lng: location.lng(),
  };
}
